import React from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { motion } from 'framer-motion';
import { QrcodeOutlined, AppleOutlined, AndroidOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import en from '../locales/en';
import zh from '../locales/zh';
import { Helmet } from 'react-helmet';
import { message } from 'antd';

const DownloadContainer = styled.div`
  min-height: 100vh;
  background-color: #fff;
  color: #333;
`;

const HeroSection = styled.section`
  padding: 120px 24px;
  text-align: center;
  background: linear-gradient(135deg, #fff 0%, #f5f9ff 100%);
  
  @media (min-width: 768px) {
    padding: 160px 40px;
  }
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 24px;
  color: #1976d2;
  
  @media (min-width: 768px) {
    font-size: 64px;
  }
`;

const Subtitle = styled.p`
  font-size: 24px;
  color: #666;
  max-width: 600px;
  margin: 0 auto 48px;
  line-height: 1.4;
`;

const QRSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
  text-align: center;
`;

const QRContainer = styled(motion.div)`
  background: #fff;
  border-radius: 24px;
  padding: 48px;
  display: inline-block;
  margin-bottom: 48px;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.1);
  border: 1px solid rgba(25, 118, 210, 0.1);
`;

const QRCode = styled.div`
  width: 200px;
  height: 200px;
  margin: 0 auto 24px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.1);
  
  img {
    width: 180px;
    height: 180px;
  }
`;

const QRText = styled.p`
  color: #1976d2;
  font-size: 18px;
  margin-bottom: 8px;
  font-weight: 500;
`;

const QRDescription = styled.p`
  color: #666;
  font-size: 16px;
`;

const AppStoreSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
  text-align: center;
  background: linear-gradient(135deg, #f5f9ff 0%, #fff 100%);
`;

const DownloadButtons = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  margin-top: 40px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
`;

const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #1976d2;
  padding: 16px 32px;
  border-radius: 12px;
  color: #fff;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
  
  &:hover {
    background: #1565c0;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(25, 118, 210, 0.3);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
    justify-content: center;
  }
  
  .anticon {
    font-size: 24px;
  }
`;

const ButtonText = styled.div`
  display: flex;
  flex-direction: column;
  
  span:first-child {
    font-size: 12px;
    opacity: 0.9;
  }
  
  span:last-child {
    font-size: 16px;
    font-weight: 500;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 1200px;
  margin: 80px auto;
  padding: 0 24px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureCard = styled(motion.div)`
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  text-align: left;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.1);
  border: 1px solid rgba(25, 118, 210, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(25, 118, 210, 0.15);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  color: #1976d2;
  margin-bottom: 16px;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

function Download() {
  const { currentLanguage } = useLanguage();
  const t = currentLanguage === 'en' ? en : zh;

  return (
    <>
      <Helmet>
        <link rel="icon" href="/favicon.ico" />
        <title>Download Kalimba Flows - Available on App Store and Android Store</title>
        <meta name="description" content="Download Kalimba Flows app for iOS and Android. Learn to play kalimba with our interactive play-along guide and tab editor." />
        <meta name="keywords" content="kalimba app download, kalimba flows app, kalimba learning app, kalimba play along app, kalimba tabs app, thumb piano app" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Download Kalimba Flows - Available on App Store and Android Store" />
        <meta property="og:description" content="Download Kalimba Flows app for iOS and Android. Learn to play kalimba with our interactive play-along guide and tab editor." />
        <meta property="og:site_name" content="Kalimba Flows" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Download Kalimba Flows - Available on App Store and Android Store" />
        <meta name="twitter:description" content="Download Kalimba Flows app for iOS and Android. Learn to play kalimba with our interactive play-along guide and tab editor." />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "Kalimba Flows",
              "operatingSystem": ["iOS", "Android"],
              "applicationCategory": "MusicEducation",
              "offers": {
                "@type": "Offer",
                "category": "Mobile Application"
              },
              "downloadUrl": {
                "iOS": "https://apps.apple.com/us/app/id6743011079",
                "Android": "https://play.google.com/store/apps/details?id=com.kalimbaflows.android"
              }
            }
          `}
        </script>
      </Helmet>
      <DownloadContainer>
        <HeroSection>
          <Title>{t.download.hero.title}</Title>
          <Subtitle>{t.download.hero.subtitle}</Subtitle>
        </HeroSection>

        <AppStoreSection>
          <DownloadButtons>
            <DownloadButton href="https://apps.apple.com/us/app/id6743011079" target="_blank">
              <AppleOutlined />
              <ButtonText>
                <span>{t.download.platforms.appStore}</span>
              </ButtonText>
            </DownloadButton>
            <DownloadButton href={"https://github.com/bluedrinker/empty/releases/latest/download/kalimbaflows.apk"} target="_blank">
              <AndroidOutlined />
              <ButtonText>
                <span>{t.download.platforms.googlePlay}</span>
              </ButtonText>
            </DownloadButton>
          </DownloadButtons>
        </AppStoreSection>
        
        {/**<QRSection>
          <FeatureGrid>
            <FeatureCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FeatureTitle>{t.download.features.instant.title}</FeatureTitle>
              <FeatureDescription>
                {t.download.features.instant.description}
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <FeatureTitle>{t.download.features.sync.title}</FeatureTitle>
              <FeatureDescription>
                {t.download.features.sync.description}
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <FeatureTitle>{t.download.features.support.title}</FeatureTitle>
              <FeatureDescription>
                {t.download.features.support.description}
              </FeatureDescription>
            </FeatureCard>
          </FeatureGrid>
        </QRSection>
        */}
      </DownloadContainer>
    </>
  );
}

export default Download; 